import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsIcon from '@mui/icons-material/Settings';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { SETTINGS_MODULE_GROUP_LABELS } from '@/config/settingsRegistry';
import { useActiveModule } from '@/hooks/useActiveModule';
import { localizeModuleLabel } from '@/i18n/navLabel';
import { SettingsCategoryCard } from './SettingsCategoryCard';
import { SettingsSearch } from './SettingsSearch';
import { useActiveSettingsModuleKey, useModuleSettingsSections } from './settingsAccess';
import { prefetchSettingsHubWarmup } from './settingsPrefetch';

export function SettingsPage() {
  const { t } = useTranslation('navigation');
  const activeModule = useActiveModule();
  const scopeModule = useActiveSettingsModuleKey();
  const sections = useModuleSettingsSections();

  const moduleLabel =
    scopeModule === 'PLATFORM'
      ? SETTINGS_MODULE_GROUP_LABELS.PLATFORM
      : activeModule
        ? localizeModuleLabel(activeModule, t)
        : SETTINGS_MODULE_GROUP_LABELS.PLATFORM;

  useEffect(() => {
    const routes = sections.flatMap((s) => s.masters.map((m) => m.route));
    prefetchSettingsHubWarmup(routes);
  }, [sections]);

  return (
    <div className="page-content w-full min-w-0">
      <PageHeader
        title={t('paths.settings', { defaultValue: 'Settings' })}
        description={`Configure ${moduleLabel} masters, organization and platform options.`}
        icon={SettingsIcon}
        actions={<SettingsSearch compact className="hidden sm:block" />}
      />

      {sections.length === 0 ? (
        <EmptyState
          title="No settings available"
          description={`There are no settings screens available for ${moduleLabel} with your current access.`}
        />
      ) : (
        <>
          <SettingsSearch className="mb-4 sm:hidden" />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sections.map(({ category, masters }) => (
              <SettingsCategoryCard
                key={category.key}
                id={category.key}
                categoryKey={category.key}
                label={category.label}
                icon={category.icon}
                masters={masters}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
